import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  PoundSterling,
  Copy,
  CheckCircle2,
  PauseCircle,
  PlayCircle,
  Loader2,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  Landmark,
  Mail,
  Globe,
  Ban,
  RotateCcw,
  UserPlus,
  Hourglass,
  Inbox,
  Trash2,
} from "lucide-react";
import api from "../lib/api";

const STATUS_OPTIONS = ["pending", "approved", "suspended"];

const STATE_STYLE = {
  pending: "#F7B500",
  available: "#A6FF00",
  requested: "#F7B500",
  paid: "#5EEAD4",
  held: "#EF4444",
  voided: "#9CA8B8",
  cancelled: "#9CA8B8",
  refunded_after_payout: "#EF4444",
};

function LedgerPanel({ affiliate, onChanged }) {
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/affiliates/${affiliate.id}/commissions`);
      setLedger(data.ledger);
    } catch {
      setLedger([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliate.id]);

  const act = async (orderId, path, body, okMsg) => {
    setBusy(orderId);
    try {
      await api.post(`/admin/affiliates/${affiliate.id}/orders/${orderId}/${path}`, body || {});
      toast.success(okMsg);
      setVoiding(null);
      setReason("");
      await load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-5 p-4 border border-[#1E2E44] bg-[#0B1220] rounded-sm" data-testid={`admin-ledger-panel-${affiliate.slug}`}>
      <div className="pg-micro mb-3 flex items-center gap-2">
        <PoundSterling size={12} /> // Commission ledger — void anything that didn't qualify before it's cashed out
      </div>
      {!ledger ? (
        <div className="text-[#9CA8B8] text-xs">Loading…</div>
      ) : ledger.length === 0 ? (
        <div className="text-[#9CA8B8] text-xs">No referred paid orders yet.</div>
      ) : (
        <div className="space-y-2">
          {ledger.map((l) => {
            const canVoid = ["pending", "available", "held"].includes(l.state);
            return (
              <div
                key={l.order_id}
                className="flex items-start justify-between gap-3 flex-wrap p-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm"
                data-testid={`admin-ledger-row-${l.order_number}`}
              >
                <div className="text-[13px] min-w-[240px]">
                  <div className="text-[#F7FAFC] pg-mono text-xs">
                    {l.order_number} · {l.date} · £{l.base_gbp.toFixed(2)} → <span className="text-[#A6FF00]">£{l.commission_gbp.toFixed(2)}</span>
                  </div>
                  <div className="text-[#9CA8B8] text-xs mt-0.5">
                    {l.customer_email} · {l.postcode || "no postcode"}
                  </div>
                  <div className="pg-mono text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: STATE_STYLE[l.state] || "#9CA8B8" }} data-testid={`admin-ledger-state-${l.order_number}`}>
                    {l.state_label}
                    {l.state === "pending" ? ` · clears ${String(l.clears_at).slice(0, 10)}` : ""}
                    {l.state === "voided" && l.void_reason ? ` · ${l.void_reason}` : ""}
                  </div>
                  {l.flags.length > 0 && !l.flags_cleared && (
                    <ul className="mt-1 space-y-0.5">
                      {l.flag_labels.map((f) => (
                        <li key={f} className="text-[#EF4444] text-[11px]">⚑ {f}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {l.flags.length > 0 && !l.flags_cleared && l.state === "held" && (
                    <button
                      onClick={() => act(l.order_id, "clear-flags", null, "Order marked genuine — commission released")}
                      disabled={busy === l.order_id}
                      className="pg-btn-ghost text-[11px]"
                      data-testid={`admin-clear-flags-${l.order_number}`}
                    >
                      {busy === l.order_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Mark genuine
                    </button>
                  )}
                  {canVoid && voiding !== l.order_id && (
                    <button
                      onClick={() => setVoiding(l.order_id)}
                      className="pg-btn-ghost text-[11px]"
                      data-testid={`admin-void-${l.order_number}`}
                    >
                      <Ban size={12} /> Void
                    </button>
                  )}
                  {voiding === l.order_id && (
                    <div className="flex items-center gap-2 flex-wrap" data-testid={`admin-void-form-${l.order_number}`}>
                      <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (e.g. refunded, self-referral)"
                        className="pg-input text-xs py-1.5 w-[220px]"
                        data-testid={`admin-void-reason-${l.order_number}`}
                      />
                      <button
                        onClick={() => act(l.order_id, "void", { reason }, "Commission voided")}
                        disabled={busy === l.order_id}
                        className="pg-btn-ghost text-[11px] text-[#EF4444]"
                        data-testid={`admin-void-confirm-${l.order_number}`}
                      >
                        {busy === l.order_id ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                        Confirm void
                      </button>
                      <button onClick={() => setVoiding(null)} className="pg-btn-ghost text-[11px]">
                        Cancel
                      </button>
                    </div>
                  )}
                  {l.state === "voided" && (
                    <button
                      onClick={() => act(l.order_id, "restore", null, "Commission restored to the pot")}
                      disabled={busy === l.order_id}
                      className="pg-btn-ghost text-[11px]"
                      data-testid={`admin-restore-${l.order_number}`}
                    >
                      <RotateCcw size={12} /> Restore
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ApplicationsInbox({ onApproved }) {
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showHandled, setShowHandled] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/affiliate-applications");
      setApps(data || []);
    } catch {
      setApps([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const act = async (id, path, okMsg) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/affiliate-applications/${id}/${path}`);
      toast.success(data?.slug ? `${okMsg} — code ${data.slug}, welcome email sent` : okMsg);
      await load();
      if (path === "approve") onApproved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  if (!apps) return null;
  const pending = apps.filter((a) => a.status === "pending");
  const handled = apps.filter((a) => a.status !== "pending");
  if (apps.length === 0) return null;

  return (
    <div className="pg-card p-4 sm:p-5 mb-6" data-testid="admin-applications-inbox">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="pg-micro flex items-center gap-2">
          <Inbox size={12} /> // Invite requests
          <span className="px-1.5 py-0.5 rounded-sm text-[#0B1220] bg-[#A6FF00] font-bold" data-testid="admin-applications-count">{pending.length}</span>
        </div>
        {handled.length > 0 && (
          <button onClick={() => setShowHandled((v) => !v)} className="pg-btn-ghost text-[11px]" data-testid="admin-applications-toggle-handled">
            {showHandled ? "Hide" : "Show"} {handled.length} handled
          </button>
        )}
      </div>
      {pending.length === 0 && <div className="text-[#9CA8B8] text-xs">No requests waiting.</div>}
      <ul className="divide-y divide-[#1E2E44]">
        {[...pending, ...(showHandled ? handled : [])].map((a) => (
          <li key={a.id} className="py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`admin-application-${a.id}`}>
            <div className="min-w-0 text-[13px]">
              <div className="text-[#F7FAFC]">{a.name} <span className="text-[#9CA8B8]">· {a.email}</span></div>
              <div className="text-[#9CA8B8] text-xs mt-0.5">
                {(a.created_at || "").slice(0, 10)}
                {a.website ? ` · ${a.website}` : ""}
                {a.audience ? ` · ${a.audience}` : ""}
              </div>
              {a.status !== "pending" && (
                <div className="pg-mono text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: a.status === "approved" ? "#5EEAD4" : "#9CA8B8" }}>
                  {a.status}{a.slug ? ` · ${a.slug}` : ""}
                </div>
              )}
            </div>
            {a.status === "pending" && (
              <div className="flex items-center gap-2">
                <button onClick={() => act(a.id, "approve", "Approved")} disabled={busy === a.id} className="pg-btn-primary text-[11px]" data-testid={`admin-application-approve-${a.id}`}>
                  {busy === a.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve & send link
                </button>
                <button onClick={() => act(a.id, "decline", "Declined")} disabled={busy === a.id} className="pg-btn-ghost text-[11px]" data-testid={`admin-application-decline-${a.id}`}>
                  <Ban size={12} /> Decline
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function InviteForm({ onInvited }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", website: "", audience: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/affiliates", {
        name: form.name,
        email: form.email,
        website: form.website || null,
        audience: form.audience || null,
      });
      toast.success(`Invited — code ${data.slug}, welcome email sent to ${form.email}`);
      setForm({ name: "", email: "", website: "", audience: "" });
      setOpen(false);
      onInvited();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not invite affiliate");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="pg-btn-primary text-xs" data-testid="admin-invite-open">
        <UserPlus size={14} /> Invite affiliate
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="pg-card p-5 w-full lg:w-[520px]" data-testid="admin-invite-form">
      <div className="pg-micro mb-3">// Invite-only onboarding (public signups are paused)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Full name"
          className="pg-input text-sm"
          data-testid="admin-invite-name"
        />
        <input
          required
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email"
          className="pg-input text-sm"
          data-testid="admin-invite-email"
        />
        <input
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder="Website / handle (optional)"
          className="pg-input text-sm"
          data-testid="admin-invite-website"
        />
        <input
          value={form.audience}
          onChange={(e) => setForm({ ...form, audience: e.target.value })}
          placeholder="Where they'll share (optional)"
          className="pg-input text-sm"
          data-testid="admin-invite-audience"
        />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="submit" disabled={saving} className="pg-btn-primary text-xs" data-testid="admin-invite-submit">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Create & email link
        </button>
        <button type="button" onClick={() => setOpen(false)} className="pg-btn-ghost text-xs">
          Cancel
        </button>
      </div>
    </form>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: { bg: "rgba(166,255,0,0.08)", bd: "rgba(166,255,0,0.35)", c: "#A6FF00" },
    pending: { bg: "rgba(247,181,0,0.08)", bd: "rgba(247,181,0,0.35)", c: "#F7B500" },
    suspended: { bg: "rgba(239,68,68,0.08)", bd: "rgba(239,68,68,0.35)", c: "#EF4444" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
      style={{ background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}
    >
      {status}
    </span>
  );
}

function BankPayable({ a }) {
  if (!a.bank_details_complete) {
    return (
      <span
        className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
        style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)", color: "#EF4444" }}
        data-testid={`admin-bank-missing-${a.slug}`}
      >
        Bank missing
      </span>
    );
  }
  return (
    <span
      className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
      style={{ background: "rgba(94,234,212,0.08)", border: "1px solid rgba(94,234,212,0.35)", color: "#5EEAD4" }}
      data-testid={`admin-bank-ok-${a.slug}`}
    >
      Payable
    </span>
  );
}

function Money({ v, color = "#F7FAFC" }) {
  return <span style={{ color }}>£{Number(v || 0).toFixed(2)}</span>;
}

export default function AdminAffiliates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliates");
      setRows(data || []);
    } catch {
      toast.error("Could not load affiliates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/affiliates/${id}`, { status });
      toast.success(`Status updated to ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const removeAffiliate = async (r) => {
    const ok = window.confirm(
      `Delete affiliate ${r.name} (${r.slug})?\n\nThis removes their account and cash-out history and un-tags their referred orders. It cannot be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/admin/affiliates/${r.id}`);
      toast.success(`Deleted ${r.slug} — ${data.orders_unstamped} order(s) un-tagged`);
      setExpanded(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const copyLink = async (slug) => {
    const url = `https://peptigenlabs.co.uk/?ref=${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 1600);
      toast.success(`Copied ${slug}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
  const activeCount = rows.filter((r) => r.status === "approved").length;

  return (
    <div data-testid="admin-affiliates-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Admin · Affiliate Programme</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Affiliates
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1 max-w-2xl">
            10% of items paid goes into each affiliate's pot per order — pending 5 days, then available to cash out as
            instant store credit (×1.5) or a bank transfer you approve. Void anything that didn't qualify while it's
            pending.
          </p>
        </div>
        <InviteForm onInvited={load} />
      </div>

      <ApplicationsInbox onApproved={load} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><Users size={12} /> // Active</div>
          <div className="text-3xl pg-heading text-[#F7FAFC]" style={{ fontFamily: "'Rajdhani'" }}>{activeCount}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><Hourglass size={12} /> // Pending</div>
          <div className="text-3xl pg-heading text-[#F7B500]" style={{ fontFamily: "'Rajdhani'" }} data-testid="admin-aff-total-pending">£{sum("pending_gbp").toFixed(2)}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><PoundSterling size={12} /> // Available</div>
          <div className="text-3xl pg-heading text-[#A6FF00]" style={{ fontFamily: "'Rajdhani'" }} data-testid="admin-aff-total-available">£{sum("available_gbp").toFixed(2)}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><Landmark size={12} /> // Awaiting approval</div>
          <div className="text-3xl pg-heading text-[#F7B500]" style={{ fontFamily: "'Rajdhani'" }} data-testid="admin-aff-total-requested">£{sum("requested_gbp").toFixed(2)}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><PoundSterling size={12} /> // Paid out</div>
          <div className="text-3xl pg-heading text-[#5EEAD4]" style={{ fontFamily: "'Rajdhani'" }}>£{sum("paid_out_value_gbp").toFixed(2)}</div>
        </div>
      </div>

      <div className="pg-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#9CA8B8] text-sm">
            <Loader2 size={18} className="animate-spin text-[#00C2D6] mx-auto mb-3" /> Loading affiliates…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#9CA8B8] text-sm">
            <ShieldAlert size={18} className="text-[#00C2D6] mx-auto mb-3" />
            No affiliates yet. Use “Invite affiliate” above.
          </div>
        ) : (
          <>
          <ul className="md:hidden divide-y divide-[#1E2E44]" data-testid="admin-affiliates-cards">
            {rows.map((r) => {
              const isOpen = expanded === r.id;
              return (
                <li key={r.id} className="p-4" data-testid={`admin-affiliate-card-${r.slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[#F7FAFC] text-sm truncate">{r.name}</div>
                      <div className="text-[#9CA8B8] text-xs truncate">{r.email}</div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <button onClick={() => copyLink(r.slug)} className="inline-flex items-center gap-1 text-[#A6FF00] pg-mono text-xs" data-testid={`admin-affiliate-copy-m-${r.slug}`}>
                          {copiedSlug === r.slug ? <CheckCircle2 size={11} /> : <Copy size={11} />} {r.slug}
                        </button>
                        <StatusPill status={r.status} />
                        <BankPayable a={r} />
                      </div>
                    </div>
                    <button onClick={() => setExpanded(isOpen ? null : r.id)} className="text-[#9CA8B8] shrink-0 p-1" data-testid={`admin-affiliate-expand-m-${r.slug}`} aria-label="Toggle">
                      {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    <div className="p-2 bg-[#0B1220] border border-[#1E2E44] rounded-sm"><div className="pg-micro">Pending</div><div className="pg-mono text-sm"><Money v={r.pending_gbp} color="#F7B500" /></div></div>
                    <div className="p-2 bg-[#0B1220] border border-[#1E2E44] rounded-sm"><div className="pg-micro">Available</div><div className="pg-mono text-sm"><Money v={r.available_gbp} color="#A6FF00" /></div></div>
                    <div className="p-2 bg-[#0B1220] border border-[#1E2E44] rounded-sm"><div className="pg-micro">Paid out</div><div className="pg-mono text-sm"><Money v={r.paid_out_value_gbp} color="#5EEAD4" /></div></div>
                  </div>
                  {(r.requested_gbp || 0) > 0 && <div className="text-[#F7B500] text-[11px] pg-mono mt-2">£{Number(r.requested_gbp).toFixed(2)} bank transfer awaiting approval</div>}
                  {(r.held_gbp || 0) > 0 && <div className="text-[#EF4444] text-[11px] pg-mono mt-1">£{Number(r.held_gbp).toFixed(2)} held · {r.flagged_count} flagged</div>}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {STATUS_OPTIONS.filter((s) => s !== r.status).map((s) => (
                      <button key={s} onClick={() => changeStatus(r.id, s)} className="pg-btn-ghost text-[10px] uppercase tracking-wider" data-testid={`admin-affiliate-set-m-${s}-${r.slug}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  {isOpen && <LedgerPanel affiliate={r} onChanged={load} />}
                </li>
              );
            })}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="pg-micro text-left border-b border-[#1E2E44]">
                  <th className="p-3 w-8"></th>
                  <th className="p-3">Name / email</th>
                  <th className="p-3">Slug</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Bank</th>
                  <th className="p-3">Pending</th>
                  <th className="p-3">Available</th>
                  <th className="p-3">Paid out</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="border-b border-[#1E2E44]/50" data-testid={`admin-affiliate-row-${r.slug}`}>
                        <td className="p-3 align-top">
                          <button
                            onClick={() => setExpanded(isOpen ? null : r.id)}
                            className="text-[#9CA8B8] hover:text-[#00C2D6]"
                            data-testid={`admin-affiliate-expand-${r.slug}`}
                            aria-label={isOpen ? "Collapse" : "Expand"}
                          >
                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                        <td className="p-3 align-top">
                          <div className="text-[#F7FAFC]">{r.name}</div>
                          <div className="text-[#9CA8B8] text-xs">{r.email}</div>
                          {r.website && <div className="text-[#00C2D6] text-xs mt-0.5 truncate max-w-[200px]">{r.website}</div>}
                        </td>
                        <td className="p-3 align-top">
                          <button
                            onClick={() => copyLink(r.slug)}
                            className="inline-flex items-center gap-1.5 text-[#A6FF00] pg-mono text-xs hover:underline"
                            data-testid={`admin-affiliate-copy-${r.slug}`}
                          >
                            {copiedSlug === r.slug ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                            {r.slug}
                          </button>
                        </td>
                        <td className="p-3 align-top"><StatusPill status={r.status} /></td>
                        <td className="p-3 align-top"><BankPayable a={r} /></td>
                        <td className="p-3 align-top" data-testid={`admin-affiliate-pending-${r.slug}`}>
                          <Money v={r.pending_gbp} color="#F7B500" />
                          {(r.held_gbp || 0) > 0 && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[#EF4444] text-[11px] pg-mono" data-testid={`admin-affiliate-held-${r.slug}`}>
                              <ShieldAlert size={11} /> £{Number(r.held_gbp).toFixed(2)} held · {r.flagged_count} flagged
                            </div>
                          )}
                        </td>
                        <td className="p-3 align-top font-semibold" data-testid={`admin-affiliate-available-${r.slug}`}>
                          <Money v={r.available_gbp} color="#A6FF00" />
                          {(r.requested_gbp || 0) > 0 && (
                            <div className="text-[#F7B500] text-[11px] pg-mono mt-1">£{Number(r.requested_gbp).toFixed(2)} requested</div>
                          )}
                        </td>
                        <td className="p-3 align-top text-[#5EEAD4]">£{Number(r.paid_out_value_gbp || 0).toFixed(2)}</td>
                        <td className="p-3 align-top">
                          <div className="flex items-center gap-2 flex-wrap">
                            {STATUS_OPTIONS.filter((s) => s !== r.status).map((s) => (
                              <button
                                key={s}
                                onClick={() => changeStatus(r.id, s)}
                                className="pg-btn-ghost text-[10px] uppercase tracking-wider"
                                data-testid={`admin-affiliate-set-${s}-${r.slug}`}
                              >
                                {s === "approved" ? <PlayCircle size={12} /> : s === "suspended" ? <PauseCircle size={12} /> : null}
                                {s}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-[#1E2E44]/50 bg-[#0B1220]" data-testid={`admin-affiliate-details-${r.slug}`}>
                          <td colSpan={9} className="p-5">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                              <div>
                                <div className="pg-micro mb-2 flex items-center gap-2"><Landmark size={12} /> // Payout bank details</div>
                                {r.bank_account_holder ? (
                                  <div className="space-y-1 text-[13px]">
                                    <div><span className="text-[#9CA8B8]">Account holder:</span> <span className="text-[#F7FAFC]">{r.bank_account_holder}</span></div>
                                    {r.bank_name && <div><span className="text-[#9CA8B8]">Bank:</span> <span className="text-[#F7FAFC]">{r.bank_name}</span></div>}
                                    {r.bank_sort_code && <div><span className="text-[#9CA8B8]">Sort code:</span> <span className="text-[#F7FAFC] pg-mono">{r.bank_sort_code}</span></div>}
                                    {r.bank_account_number && <div><span className="text-[#9CA8B8]">Account #:</span> <span className="text-[#F7FAFC] pg-mono">{r.bank_account_number}</span></div>}
                                    {r.bank_iban && <div><span className="text-[#9CA8B8]">IBAN:</span> <span className="text-[#F7FAFC] pg-mono text-[11px] break-all">{r.bank_iban}</span></div>}
                                    {r.bank_swift && <div><span className="text-[#9CA8B8]">SWIFT:</span> <span className="text-[#F7FAFC] pg-mono">{r.bank_swift}</span></div>}
                                    {r.bank_country && <div><span className="text-[#9CA8B8]">Country:</span> <span className="text-[#F7FAFC]">{r.bank_country}</span></div>}
                                    {r.tax_reference && <div><span className="text-[#9CA8B8]">UTR / VAT:</span> <span className="text-[#F7FAFC] pg-mono">{r.tax_reference}</span></div>}
                                    {r.bank_updated_at && <div className="text-[#9CA8B8] text-[11px] pt-1">Last updated {r.bank_updated_at.slice(0, 10)}</div>}
                                  </div>
                                ) : (
                                  <div className="text-[#F7B500] text-[13px]">No bank details yet — they can still cash out as store credit.</div>
                                )}
                              </div>
                              <div>
                                <div className="pg-micro mb-2 flex items-center gap-2"><Globe size={12} /> // Promotion channels</div>
                                <div className="text-[13px] text-[#F7FAFC]">{r.audience || <span className="text-[#9CA8B8]">— Not supplied —</span>}</div>
                                {r.website && (
                                  <a
                                    href={r.website.startsWith("http") ? r.website : `https://${r.website}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-block mt-2 text-[#00C2D6] text-xs pg-mono break-all hover:underline"
                                  >
                                    {r.website}
                                  </a>
                                )}
                              </div>
                              <div>
                                <div className="pg-micro mb-2 flex items-center gap-2"><Mail size={12} /> // Account</div>
                                <div className="text-[13px] space-y-1">
                                  <div><span className="text-[#9CA8B8]">Joined:</span> <span className="text-[#F7FAFC]">{(r.created_at || "").slice(0, 10)}</span>{r.source === "admin_invite" ? <span className="text-[#9CA8B8]"> · invited</span> : r.source === "application" ? <span className="text-[#9CA8B8]"> · applied</span> : null}</div>
                                  <div><span className="text-[#9CA8B8]">All-time commission:</span> <span className="text-[#F7FAFC]">£{Number(r.total_commission_gbp || 0).toFixed(2)}</span></div>
                                  <div><span className="text-[#9CA8B8]">Voided:</span> <span className="text-[#9CA8B8]">£{Number(r.voided_gbp || 0).toFixed(2)}</span></div>
                                  <div><span className="text-[#9CA8B8]">Rate:</span> <span className="text-[#F7FAFC]">10% per new customer · 15% as store credit</span></div>
                                </div>
                                <button
                                  onClick={() => removeAffiliate(r)}
                                  className="pg-btn-ghost text-[11px] text-[#EF4444] mt-3"
                                  data-testid={`admin-affiliate-delete-${r.slug}`}
                                >
                                  <Trash2 size={12} /> Delete affiliate
                                </button>
                              </div>
                            </div>
                            <LedgerPanel affiliate={r} onChanged={load} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
